<template>
  <Card>
    <template #content>
      <div class="flex flex-col gap-8">
        <FormGroup :label="$t('forms.device_type.device_type_properties_form.name')" :error-text="form.errors?.name">
          <InputText
              data-cy="device-type-input"
              v-model="form.name"
              @change="form.validate('name')"
              @keyup="onNameChange"
              :disabled="disabled"
          />
        </FormGroup>

        <FormGroup :label="$t('forms.device_type.device_type_properties_form.key')" :error-text="form.errors?.key">
          <InputText
              v-model="form.key"
              @change="form.validate('key')"
              :disabled="true"
          />
        </FormGroup>

        <FormGroup :label="$t('forms.device_type.device_type_properties_form.company')"
                   :error-text="form.errors?.company_ids || form.errors?.company_id">
          <div class="flex flex-col gap-4">
            <div class="flex items-center gap-3">
              <InputSwitch
                  v-model="availableToAllCompanies"
                  :disabled="disabled"
                  data-cy="available-companies-switchbtn"
              />
              <span class="text-sm text-surface-500">{{ allCompaniesLabel }}</span>
            </div>
            <MultiSelect
                v-if="!availableToAllCompanies"
                v-model="form.company_ids"
                :options="companiesStore.getCompanyOptions"
                option-label="name"
                option-value="id"
                :disabled="disabled"
                display="chip"
                class="w-full"
                @change="onCompanyIdsChange"
                data-cy="company-name-multiselection-input"
            />
          </div>
        </FormGroup>

        <FormGroup :label="$t('forms.device_type.device_type_properties_form.description')"
                   :error-text="form.errors?.description">
					<Textarea
              ref="description"
              v-model="form.description"
              @change="form.validate('description')"
              rows="5"
              :disabled="disabled"
          />
        </FormGroup>

        <FormGroup :label="$t('forms.device_type.device_type_properties_form.image')">
          <div class="flex flex-col gap-3">
            <img
                v-if="currentImageUrl"
                :src="currentImageUrl"
                class="h-24 w-auto object-contain rounded border border-surface-200"
                alt="Device Type Image"
            />
            <div class="flex items-center gap-3">
              <input
                  ref="imageInput"
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  class="hidden"
                  @change="onImageFileSelected"
                  :disabled="disabled"
              />
              <Button
                  type="button"
                  outlined
                  size="small"
                  :label="$t('forms.device_type.device_type_properties_form.choose_image')"
                  @click="$refs.imageInput.click()"
                  :disabled="disabled"
              />
              <span v-if="selectedImageFile" class="text-sm text-surface-500">{{ selectedImageFile.name }}</span>
            </div>
          </div>
        </FormGroup>
      </div>
    </template>

    <template v-if="!disabled && (hasOnSaveListener || hasOnDeleteListener)" #footer>
      <div class="flex justify-end space-x-3">
        <Button data-cy="device-type-delete-btn" class="px-8" text severity="danger" v-if="hasOnDeleteListener && form.id" @click="$emit('on-delete', form)"
                :label="$t('forms.common.delete')"
                :disabled="form.processing"/>
        <Button data-cy="device-type-save-btn" class="px-8" v-if="hasOnSaveListener" @click="emitSave(selectedImageFile)" :label="$t('forms.common.save')"
                :disabled="form.processing"/>
      </div>
    </template>
  </Card>
</template>

<script>
import {mapStores} from "pinia";
import FormGroup from "@/Components/FormGroup.vue";
import {useCompaniesStore} from "@/stores/companies.js";
import companySelectionValidation from "@/Mixins/companySelectionValidation.js";

export default {
  name: "DeviceTypePropertiesForm",
  components: {FormGroup},
  mixins: [companySelectionValidation],
  props: {
    form: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedImageFile: null,
      currentImageUrl: null,
      availableToAllCompanies: true,
    };
  },
  computed: {
    ...mapStores(useCompaniesStore),
    hasOnSaveListener() {
      return !!(this.$attrs?.onOnSave);
    },
    hasOnDeleteListener() {
      return !!(this.$attrs?.onOnDelete);
    },
    allCompaniesLabel() {
      return this.canSavePublicType
          ? this.$t('forms.common.all')
          : this.$t('forms.common.all_accessible_companies');
    },
    canSavePublicType() {
      return this.$auth.hasPermission('update:all-device-types');
    },
    companyRequiredMessage() {
      return this.$t('forms.device_type.device_type_properties_form.company_required');
    },
  },
  methods: {
    onCompanyIdsChange() {
      this.form.company_id = this.form.company_ids?.length === 1
          ? this.form.company_ids[0]
          : null;
      if (!this.validateCompanySelection()) {
        return;
      }
      this.form.validate('company_ids');
    },
    setInitialCompanyIds() {
      if (!Array.isArray(this.form.company_ids)) {
        this.form.company_ids = this.form.company_id ? [this.form.company_id] : [];
      }
      this.availableToAllCompanies = !this.form.company_ids.length;
      this.form.apply_to_all_companies = this.availableToAllCompanies;
    },
    onNameChange($event) {
      this.form.key = this.form.name?.trim().replaceAll(' ', '_').toUpperCase();
      this.form.validate?.('key');
    },
    onImageFileSelected(event) {
      this.selectedImageFile = event.target.files[0] || null;
      if (this.selectedImageFile) {
        this.currentImageUrl = URL.createObjectURL(this.selectedImageFile);
      }
    },
  },
  watch: {
    availableToAllCompanies(value) {
      this.form.apply_to_all_companies = value;
      if (value) {
        this.form.company_ids = [];
        this.form.company_id = null;
        this.clearCompanySelectionError();
        return;
      }

      this.validateCompanySelection();
    },
    'form.image_url': {
      handler(newValue) {
        this.currentImageUrl = newValue;
      },
      immediate: true,
    },
  },
  mounted() {
    this.setInitialCompanyIds();
    this.companiesStore.indexCompanyOptions();
  }
}
</script>

<style scoped>

</style>
